import { useEffect, useRef, useState } from 'react'

import { EMPTY_FUNCTION, EMPTY_OBJECT } from '../../../../constants/common'
import { GretaAIChatChatActionType } from '../../GretaAIChat.constants'

export function useGretaAIChatProjectActions({
  activeChatIdRef,
  deleteProject,
  openNewChat = EMPTY_FUNCTION,
  panelRef,
  renameProject,
} = EMPTY_OBJECT) {
  const [projectAction, setProjectAction] = useState(null)

  const returnFocusRef = useRef(null)

  // After a delete the "…" that opened the dialog is gone with its row.
  useEffect(() => {
    if (!projectAction && returnFocusRef.current) {
      const { current: returnFocusElement } = returnFocusRef
      const focusElement = returnFocusElement.isConnected
        ? returnFocusElement
        : panelRef.current

      returnFocusRef.current = null
      focusElement?.focus()
    }
  }, [panelRef, projectAction])

  // The row menu hands focus back to its "…" before a dialog opens.
  function openProjectAction(project, type) {
    returnFocusRef.current = document.activeElement
    setProjectAction({ project, type })
  }

  function openRenameProject(project) {
    openProjectAction(project, GretaAIChatChatActionType.Rename)
  }

  function openDeleteProject(project) {
    openProjectAction(project, GretaAIChatChatActionType.Delete)
  }

  function closeProjectAction() {
    setProjectAction(null)
  }

  // Esc can close the dialog while its request runs; a later dialog stays open.
  function closeSettledProjectAction(settledAction) {
    setProjectAction(current => (current === settledAction ? null : current))
  }

  async function confirmRenameProject(name) {
    const {
      project: { id },
    } = projectAction

    await renameProject(id, name)

    closeSettledProjectAction(projectAction)
  }

  async function confirmDeleteProject() {
    const {
      project: { chatIds, id },
    } = projectAction

    await deleteProject(id)

    // Ids, as the chat list may lack them; read after the request, as another chat may be open now.
    if (chatIds.includes(activeChatIdRef.current)) {
      openNewChat()
    }

    closeSettledProjectAction(projectAction)
  }

  return {
    closeProjectAction,
    confirmDeleteProject,
    confirmRenameProject,
    openDeleteProject,
    openRenameProject,
    projectAction,
  }
}
